/* eslint-disable no-comments/disallowComments */
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, FormGroup, NgControl, ValidationErrors } from '@angular/forms';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { interval, Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { SimpleField } from '../../abstracts/simple-field';
import { TimeFieldConfig } from '../../models/fields/time-field-config.model';

interface FbTimeEntity {
  hours: number | string;
  minutes: number | string;
  seconds: number | string;
}

@Component({
  selector: 'fb-timepicker2-field',
  templateUrl: './timepicker2-field.component.html',
  styleUrls: ['./timepicker2-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatFormFieldControl, useExisting: TimepickerFieldV2Component }],
})
export class TimepickerFieldV2Component
  extends SimpleField<string>
  implements OnInit, OnDestroy, MatFormFieldControl<string>
{
  @Input()
  override get required(): boolean {
    return this.isRequired;
  }
  override set required(req: boolean) {
    this.isRequired = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  get empty(): boolean {
    return !this.value;
  }

  get errorState(): boolean {
    return this.timepickerControl.invalid && this.timepickerControl.touched;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @HostBinding('id')
  get elementId(): string {
    return this.id;
  }

  @Input() set disabled(value: boolean) {
    this.disable = Boolean(value);
    if (this.disable) {
      this.timeGroup.disable();
    } else {
      this.timeGroup.enable();
    }
  }
  get disabled(): boolean {
    return this.disable;
  }

  /* eslint-disable-next-line @angular-eslint/no-input-rename */
  @Input('aria-describedby') userAriaDescribedBy: string;

  @Input() set config(config: TimeFieldConfig) {
    this.setEdgesOfTime(config);
  }
  @Input() limitTimeAfterPaste: boolean = true;
  @Input() apmMode: boolean = false;
  @Input() xAnchor: 'left' | 'right' = 'left';
  @Input() min: number = 0;
  @Input() max: number;
  @Input() format: number | string = 24;

  @ViewChild('zuumTimePicker') zuumTimePickerElement: HTMLElement;
  @ViewChild('timeInput') timeInput: ElementRef;
  @ViewChild('fb_hoursInput') fb_hoursInput: ElementRef;
  @ViewChild('fb_minutesInput') fb_minutesInput: ElementRef;
  @ViewChild('fb_secondsInput') fb_secondsInput: ElementRef;
  @ViewChild('fb_apm') fb_apm: ElementRef;

  static nextId: number = 0;

  id: string = `time-input-${TimepickerFieldV2Component.nextId++}`;

  controlType: string = 'timepicker';
  focused: boolean = false;
  timepickerControl: FormControl = new FormControl(undefined, { asyncValidators: [this.validateControl(this)] });
  timeGroup: FormGroup = new FormGroup({
    time: new FormControl(null, {
      asyncValidators: [this.validateControl(this)],
    }),
  });
  manualTimeInputGroup: FormGroup = new FormGroup({
    hours: new FormControl('00', {
      asyncValidators: [this.validateControl(this)],
    }),
    minutes: new FormControl('00', {
      asyncValidators: [this.validateControl(this)],
    }),
    seconds: new FormControl('00', {
      asyncValidators: [this.validateControl(this)],
    }),
    apm: new FormControl('AM'),
  });
  minTime: FbTimeEntity = {
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
  maxTime: FbTimeEntity = {
    hours: 23,
    minutes: 59,
    seconds: 59,
  };
  borderlessMode: boolean = false;
  threeNumbersMask: boolean = false;
  isSelected: boolean = false;

  isAm: boolean = false;

  private isRequired: boolean = false;

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() public override ngControl: NgControl,
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public formField: MatFormField,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.initListeners();
  }

  override ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  onContainerClick(): void {
    this.focusMonitor.focusVia(this.zuumTimePickerElement, 'program');
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement: HTMLElement = this.elementRef.nativeElement.querySelector('.timepicker-v2-container');
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(): void {
    this.timepickerControl.markAsTouched();
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  selectSubInput(id: 'fb_hoursInput' | 'fb_minutesInput' | 'fb_secondsInput' | 'fb_apm'): void {
    let input: HTMLInputElement;
    if (id === 'fb_hoursInput') {
      input = this.fb_hoursInput.nativeElement as HTMLInputElement;
    } else if (id === 'fb_minutesInput') {
      input = this.fb_minutesInput.nativeElement as HTMLInputElement;
    } else if (id === 'fb_secondsInput') {
      input = this.fb_secondsInput.nativeElement as HTMLInputElement;
    } else {
      input = this.fb_apm.nativeElement as HTMLInputElement;
    }
    if (!input) return;
    input.select();
    input.focus();
  }

  toggleApm(): void {
    this.isAm = !this.isAm;
    this.manualTimeInputGroup.get('apm').setValue(this.isAm ? 'AM' : 'PM', { emitEvent: false });
    this.timeGroup.patchValue(this.timeGroup.value);
  }

  onFocusAndSelect(): void {
    const input = this.timeInput.nativeElement as HTMLInputElement;
    if (!this.isSelected) {
      input.select();
      this.isSelected = true;
    }
    input.focus();
  }

  onResetFirstClick(): void {
    this.isSelected = false;
  }

  onPasteClipboard(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.disabled) return;
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');

      // Process the pasted text
      const [hours, minutes, seconds]: string[] = pastedText.split(':').map((item: string) => item);

      let hrsToSet: number = isNaN(parseInt(hours)) ? 0 : parseInt(hours);
      const minToSet: number = isNaN(parseInt(minutes)) ? 0 : parseInt(minutes);
      const secToSet: number = isNaN(parseInt(seconds)) ? 0 : parseInt(seconds);
      if (this.apmMode && !this.borderlessMode) {
        const hasPm: boolean = pastedText.toLowerCase().includes('pm');
        this.isAm = hrsToSet <= 12 && !hasPm;
        let valToFormat: string = pastedText;
        if (pastedText.toLowerCase().includes('pm') || pastedText.toLowerCase().includes('am')) {
          valToFormat = pastedText.toLowerCase().replace(' ', '').replace('am', '').replace('pm', '');
        }
        const timeIn12HrsFormat: string = this.convertTo12Hour(valToFormat);
        const [hrsF]: number[] = timeIn12HrsFormat.split(':').map((item: string) => Number(item));
        hrsToSet = hrsF;
        this.manualTimeInputGroup.get('apm').setValue(this.isAm ? 'AM' : 'PM', { emitEvent: false });
      }
      const valueToSet: FbTimeEntity = {
        hours: this.borderlessMode && hrsToSet > 99 ? hrsToSet : ('0' + hrsToSet).slice(-2),
        minutes: ('0' + minToSet).slice(-2),
        seconds: ('0' + secToSet).slice(-2),
      };
      const stringToSet: string = `${valueToSet.hours}:${valueToSet.minutes}:${valueToSet.seconds}`;
      if (this.limitTimeAfterPaste) {
        this.calculateNewMinMaxTimeAfterPaste(stringToSet);
      }
      this.manualTimeInputGroup.patchValue(valueToSet);
      this.timeGroup.get('time').setValue(stringToSet);
      //this.value = stringToSet;
    }
  }

  calculateNewMinMaxTimeAfterPaste(time: string): void {
    let timeToSet: string = time;
    if (!this.isAm) {
      timeToSet = this.convertTo24Hour(time);
    }
    const [hours, minutes, seconds]: string[] = timeToSet.split(':').map((item: string) => item);
    if (
      (hours >= this.maxTime.hours || minutes >= this.maxTime.minutes || seconds >= this.maxTime.seconds) &&
      !this.borderlessMode
    ) {
      this.maxTime = {
        hours: hours,
        minutes: minutes,
        seconds: seconds,
      };
    } else if (hours <= this.minTime.hours || minutes <= this.minTime.minutes || seconds <= this.minTime.seconds) {
      this.minTime = {
        hours: hours,
        minutes: minutes,
        seconds: seconds,
      };
    }
  }

  updateValueInMainInput(): void {
    const manualInputTimeForm: FbTimeEntity = this.manualTimeInputGroup.value;
    let timeToSet = `${manualInputTimeForm.hours}:${manualInputTimeForm.minutes}:${manualInputTimeForm.seconds}`;
    if (this.apmMode && !this.borderlessMode) {
      timeToSet = this.convertTo24Hour(timeToSet);
    }
    const [hours, minutes, seconds]: string[] = timeToSet.split(':').map((item: string) => item);
    let hrsToSet: number = isNaN(parseInt(hours)) ? 0 : parseInt(hours);
    let minToSet: number = isNaN(parseInt(minutes)) ? 0 : parseInt(minutes);
    let secToSet: number = isNaN(parseInt(seconds)) ? 0 : parseInt(seconds);

    const diffInMinMins: number = Number(minToSet) - Number(this.minTime.minutes);
    const diffInMinHrs: number = Number(hrsToSet) - Number(this.minTime.hours);

    if (Number(secToSet) < Number(this.minTime.seconds) && diffInMinMins == 0 && diffInMinHrs == 0) {
      secToSet = Number(this.minTime.seconds);
    }
    const diffInMaxMins: number = Number(this.maxTime.minutes) - Number(minToSet);
    const diffInMaxHrs: number = Number(this.maxTime.hours) - Number(hrsToSet);

    if (Number(secToSet) > Number(this.maxTime.seconds) && diffInMaxMins <= 0 && diffInMaxHrs == 0) {
      secToSet = Number(this.maxTime.seconds);
    }

    if (Number(minToSet) < Number(this.minTime.minutes) && diffInMinHrs == 0) {
      minToSet = Number(this.minTime.minutes);
    }

    if (Number(minToSet) > Number(this.maxTime.minutes) && diffInMaxHrs == 0) {
      minToSet = Number(this.maxTime.minutes);
    }

    if (hrsToSet > Number(this.maxTime.hours)) {
      hrsToSet = Number(this.maxTime.hours);
    }
    if (hrsToSet < Number(this.minTime.hours)) {
      hrsToSet = Number(this.minTime.hours);
    }
    const valueToSet: FbTimeEntity = {
      hours: this.borderlessMode && hrsToSet > 99 ? hrsToSet : ('0' + hrsToSet).slice(-2),
      minutes: ('0' + minToSet).slice(-2),
      seconds: ('0' + secToSet).slice(-2),
    };
    const stringToSet: string = `${valueToSet.hours}:${valueToSet.minutes}:${valueToSet.seconds}`;
    this.timeGroup.get('time').setValue(stringToSet);
  }

  onUpdateValue(control: 'hours' | 'minutes' | 'seconds', add: boolean): void {
    if (this.disabled) return;
    const input: AbstractControl = this.timeGroup.get('time');
    const val: string = input.value.toLowerCase().replace(' ', '').replace('am', '').replace('pm', '');
    let [hours, minutes, seconds]: number[] = val.split(':').map((item: string) => Number(item));

    if (this.apmMode && !this.borderlessMode) {
      const timeIn12HrsFormat: string = this.convertTo24Hour(val);
      const [hrsF, minsF, secsF]: number[] = timeIn12HrsFormat.split(':').map((item: string) => Number(item));
      hours = hrsF;
      minutes = minsF;
      seconds = secsF;
    }

    if (control == 'hours' && hours == 0 && !add) return;
    if (control == 'hours' && hours == 23 && add && !this.borderlessMode) return;
    if (control == 'minutes' && minutes == 0 && !add) {
      const hrsLength: number = hours.toString().length || 2;
      if (hours == this.minTime.hours) return;
      let valToSet: string = `${('0' + hours).slice(-hrsLength)}:${'59'}:${('0' + seconds).slice(-2)}`;
      if (this.apmMode && !this.borderlessMode) {
        valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
      }
      input.setValue(valToSet, { emitEvent: false });
      this.onUpdateValue('hours', false);
      return;
    }
    if (control == 'seconds' && seconds == 59 && add) {
      const hrsLength: number = hours.toString().length || 2;
      if (hours == this.maxTime.hours && minutes == this.maxTime.minutes) return;
      let valToSet: string = `${('0' + hours).slice(-hrsLength)}:${('0' + minutes).slice(-2)}:${'00'}`;
      if (this.apmMode && !this.borderlessMode) {
        valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
      }
      input.setValue(valToSet, { emitEvent: false });
      this.onUpdateValue('minutes', true);
      return;
    }
    if (control == 'seconds' && seconds == 0 && !add) {
      const hrsLength: number = hours.toString().length || 2;
      if (hours == this.minTime.hours && minutes == this.minTime.minutes) return;
      let valToSet: string = `${('0' + hours).slice(-hrsLength)}:${('0' + minutes).slice(-2)}:${'59'}`;
      if (this.apmMode && !this.borderlessMode) {
        valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
      }
      input.setValue(valToSet, { emitEvent: false });
      this.onUpdateValue('minutes', false);
      return;
    }

    let valueToSet: string;
    if (control == 'hours') {
      const val: string = add
        ? this.borderlessMode && hours + 1 > 99
          ? (hours + 1).toString()
          : ('0' + (hours + 1)).slice(-2)
        : this.borderlessMode && hours - 1 > 99
        ? (hours - 1).toString()
        : ('0' + (hours - 1)).slice(-2);

      if (this.apmMode && !this.borderlessMode) {
        if (Number(val) >= 12) {
          this.isAm = false;
        } else {
          this.isAm = true;
        }
      }

      valueToSet = `${val}:${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`;
    }
    if (control == 'minutes') {
      const val: string = ('0' + (add ? minutes + 1 : minutes - 1)).slice(-2);
      valueToSet = `${hours}:${val}:${('0' + seconds).slice(-2)}`;
    }
    if (control == 'seconds') {
      const val: string = ('0' + (add ? seconds + 1 : seconds - 1)).slice(-2);
      valueToSet = `${hours}:${('0' + minutes).slice(-2)}:${val}`;
    }

    if (this.apmMode && !this.borderlessMode) {
      valueToSet = this.convertTo12Hour(valueToSet);
    }

    input.setValue(valueToSet);
  }

  protected override onWriteValue(value: string): void {
    if (!(value === undefined || value === null || value === '')) {
      const val: string = this.value.toLowerCase().replace(' ', '').replace('am', '').replace('pm', '');
      const [hrs, mins, secs]: number[] = val.split(':').map((item: string) => Number(item));
      if (hrs > 99 && this.borderlessMode) {
        this.threeNumbersMask = true;
      } else {
        this.threeNumbersMask = false;
      }
      let hours: number = hrs;
      let minutes: number = mins;
      let seconds: number = secs;
      if (this.apmMode && !this.borderlessMode) {
        if (hours >= 12) {
          this.isAm = false;
        } else {
          this.isAm = true;
        }
        const timeIn12HrsFormat: string = this.convertTo12Hour(val);
        const [hrsF, minsF, secsF]: number[] = timeIn12HrsFormat.split(':').map((item: string) => Number(item));
        hours = hrsF;
        minutes = minsF;
        seconds = secsF;
        this.manualTimeInputGroup.get('apm').setValue(this.isAm ? 'AM' : 'PM', { emitEvent: false });
      }

      const valueToSet: FbTimeEntity = {
        hours: this.borderlessMode && hours > 99 ? hours : ('0' + hours).slice(-2),
        minutes: ('0' + minutes).slice(-2),
        seconds: ('0' + seconds).slice(-2),
      };
      this.manualTimeInputGroup.patchValue(valueToSet);
      let valToSet: string = `${valueToSet.hours}:${valueToSet.minutes}:${valueToSet.seconds}`;
      if (this.apmMode && !this.borderlessMode) {
        valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
      }
      this.timeGroup.patchValue({ time: valToSet }, { emitEvent: false });
    } else {
      this.timeGroup.reset();
      this.timeGroup.setErrors(null);
    }
  }

  private parseTime(time: string): FbTimeEntity {
    const [hours, minutes, seconds]: number[] = time.split(':').map((item: string) => Number(item));
    return {
      hours: hours,
      minutes: minutes,
      seconds: seconds,
    };
  }

  private setEdgesOfTime(config: TimeFieldConfig): void {
    this.borderlessMode = config.borderlessMode;
    if (this.borderlessMode) {
      this.maxTime.hours = 9999;
    }
    if (config.minTime) {
      this.minTime = this.parseTime(config.minTime);
    }
    if (config.maxTime) {
      this.maxTime = this.parseTime(config.maxTime);
    }
  }

  private validateControl(component: TimepickerFieldV2Component): AsyncValidatorFn {
    return (): Observable<ValidationErrors | null> => {
      return interval(1).pipe(
        take(1),
        map(() =>
          !component.ngControl?.control || component.ngControl.control.valid
            ? null
            : component.ngControl.control.errors,
        ),
      );
    };
  }

  private initListeners(): void {
    this.manualTimeInputGroup
      .get('apm')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((data: string | null) => {
        if (!data) {
          this.manualTimeInputGroup.get('apm').setValue(this.isAm ? 'AM' : 'PM', { emitEvent: false });
          return;
        }
        if (data.toLowerCase().startsWith('p')) {
          this.isAm = false;
          this.manualTimeInputGroup.get('apm').setValue('PM', { emitEvent: false });
        } else if (data.toLowerCase().startsWith('a')) {
          this.isAm = true;
          this.manualTimeInputGroup.get('apm').setValue('AM', { emitEvent: false });
        } else {
          const valToSet: 'AM' | 'PM' = this.isAm ? 'AM' : 'PM';
          this.manualTimeInputGroup.get('apm').setValue(valToSet, { emitEvent: false });
        }
        this.timeGroup.patchValue(this.timeGroup.value);
      });
    this.timeGroup.valueChanges
      .pipe(untilComponentDestroyed(this), distinctUntilChanged())
      .subscribe((value: { time: string }) => {
        const val: string = value.time.toLowerCase().replace(' ', '').replace('am', '').replace('pm', '');
        let [hours, minutes, seconds]: string[] = val.split(':').map((item: string) => item);
        if (this.apmMode && !this.borderlessMode) {
          const timeIn12HrsFormat: string = this.convertTo24Hour(val);
          const [hrsF, minsF, secsF]: string[] = timeIn12HrsFormat.split(':');
          hours = hrsF;
          minutes = minsF;
          seconds = secsF;
        }
        const diffInMinMins: number = Number(minutes) - Number(this.minTime.minutes);
        const diffInMinHrs: number = Number(hours) - Number(this.minTime.hours);

        if (
          Number(seconds) < Number(this.minTime.seconds) &&
          ((diffInMinMins == 0 && diffInMinHrs == 0) || (diffInMinMins < 0 && diffInMinHrs <= 0))
        ) {
          const sec: string = ('0' + this.minTime.seconds).slice(-2);
          let valToSet: string = this.getTimeToSet(hours, minutes, sec);
          if (this.apmMode && !this.borderlessMode) {
            valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
          }
          this.timeGroup.get('time').setValue(valToSet, { emitEvent: false });
          seconds = sec;
        }
        const diffInMaxMins: number = Number(this.maxTime.minutes) - Number(minutes);
        const diffInMaxHrs: number = Number(this.maxTime.hours) - Number(hours);
        if (Number(seconds) > Number(this.maxTime.seconds) && diffInMaxMins <= 0 && diffInMaxHrs <= 0) {
          const sec: string = ('0' + this.maxTime.seconds).slice(-2);
          let valToSet: string = this.getTimeToSet(hours, minutes, sec);
          if (this.apmMode && !this.borderlessMode) {
            valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
          }
          this.timeGroup.get('time').setValue(valToSet, { emitEvent: false });
          seconds = sec;
        }

        if (Number(seconds) == 60 && ((diffInMaxMins == 0 && diffInMaxHrs != 0) || diffInMaxMins > 0)) {
          seconds = '00';
          minutes = (Number(minutes) + 1).toString();
        }

        if (Number(minutes) < Number(this.minTime.minutes) && diffInMinHrs <= 0) {
          const min: string = ('0' + this.minTime.minutes).slice(-2);
          let valToSet: string = this.getTimeToSet(hours, min, seconds);
          if (this.apmMode && !this.borderlessMode) {
            valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
          }
          this.timeGroup.get('time').setValue(valToSet, { emitEvent: false });
          minutes = min;
        }

        if (Number(minutes) > Number(this.maxTime.minutes) && diffInMaxHrs <= 0) {
          const min: string = ('0' + this.maxTime.minutes).slice(-2);
          let valToSet: string = this.getTimeToSet(hours, min, seconds);
          if (this.apmMode && !this.borderlessMode) {
            valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
          }
          this.timeGroup.get('time').setValue(valToSet, { emitEvent: false });
          minutes = min;
        }

        if (Number(minutes) == 60 && diffInMaxHrs != 0) {
          minutes = '00';
          hours = (Number(hours) + 1).toString();
        }

        if (Number(hours) < Number(this.minTime.hours)) {
          const hrsLength: number = this.minTime.hours.toString().length || 2;
          const hrs: string = ('0' + this.minTime.hours).slice(-hrsLength);
          let valToSet: string = this.getTimeToSet(hrs, minutes, seconds);
          if (this.apmMode && !this.borderlessMode) {
            valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
          }
          this.timeGroup.get('time').setValue(valToSet, { emitEvent: false });
          hours = hrs;
        }
        if (Number(hours) > Number(this.maxTime.hours)) {
          const hrsLength: number = this.maxTime.hours.toString().length || 2;
          const hrs: string = ('0' + this.maxTime.hours).slice(-hrsLength);
          let valToSet: string = this.getTimeToSet(hrs, minutes, seconds);
          if (this.apmMode && !this.borderlessMode) {
            valToSet += ` ${this.isAm ? 'AM' : 'PM'}`;
          }
          this.timeGroup.get('time').setValue(valToSet, { emitEvent: false });
          hours = hrs;
        }
        if (Number(hours) > 99 && this.borderlessMode) {
          this.threeNumbersMask = true;
        } else {
          this.threeNumbersMask = false;
        }
        const valToSet: string = this.getTimeToSet(hours, minutes, seconds);
        let valToSetFormatted: string = valToSet;
        if (this.apmMode && !this.borderlessMode) {
          if (Number(hours) >= 12) {
            this.isAm = false;
          } else {
            this.isAm = true;
          }
          valToSetFormatted = this.convertTo12Hour(valToSet);
        }
        const [hrs, mins, secs]: string[] = valToSetFormatted.split(':');
        this.manualTimeInputGroup.patchValue({ hours: hrs, minutes: mins, seconds: secs });
        if (this.apmMode && !this.borderlessMode) {
          valToSetFormatted += ` ${this.isAm ? 'AM' : 'PM'}`;
        }
        this.manualTimeInputGroup.get('apm').setValue(this.isAm ? 'AM' : 'PM', { emitEvent: false });
        this.timeGroup.get('time').setValue(valToSetFormatted, { emitEvent: false });
        this.value = valToSet;
      });
  }

  private getTimeToSet(h: string, m: string, s: string): string {
    return `${this.borderlessMode && +h > 99 ? h : ('0' + h).slice(-2) || '00'}:${('0' + m).slice(-2) || '00'}:${
      ('0' + s).slice(-2) || '00'
    }`;
  }

  private convertTo24Hour(time12h: string): string {
    // Split time into its components (time and AM/PM)
    const [hours, minutes, seconds]: number[] = time12h.split(':').map((item: string) => Number(item));
    // Split the time into hours, minutes, and seconds
    let hr: number = hours;
    // If the modifier is PM and the hours are less than 12, add 12 to convert to 24-hour format
    if (this.isAm && hr === 12) {
      hr = 0;
    }

    // If it's PM and the hours are less than 12, add 12 (e.g. 1 PM becomes 13)
    if (!this.isAm && hr < 12) {
      hr += 12;
    }
    // Ensure the hours, minutes, and seconds are in two-digit format
    const hoursFormatted = hr < 10 ? `0${hr}` : hr;
    const minutesFormatted = minutes;
    const secondsFormatted = seconds;

    // Return the formatted time in 24-hour format
    return `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
  }

  private convertTo12Hour(time24h: string): string {
    // Split the input time into hours, minutes, and seconds
    const [hours, minutes, seconds]: string[] = time24h.split(':');

    // Convert the string values into integers
    let hr: number = parseInt(hours, 10);

    // Convert hours from 24-hour to 12-hour format
    hr = hr % 12 || 12; // Convert "0" or "12" to "12" for midnight/noon

    // Ensure the hours, minutes, and seconds are in two-digit format
    const hoursFormatted = hr < 10 ? `0${hr}` : hr;
    const minutesFormatted = minutes;
    const secondsFormatted = seconds;

    // Return the formatted time with AM/PM
    return `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
  }
}
